import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChartLine, ArrowRight } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import Collapsible from './Collapsible';
import './ResidentStats.css';

// Srpska množina: 1 stanar, 2-4 stanara, 5+ stanara
const plural = (count, one, few, many) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};

// ===========================================
// Grafikoni (bez dodatnih biblioteka - čist SVG/CSS)
// ===========================================
const CHART_COLORS = ['#9fb746', '#0f62fe', '#f1c21b', '#da1e28', '#24a148'];

const numberFormat = new Intl.NumberFormat('sr-RS');
const fmt = (n) => (n === null || n === undefined ? '0' : numberFormat.format(n));

// Donut - raspodela kreveta (zauzeti / slobodni) sa procentom u sredini
const DonutChart = ({ items = [], size = 120, thickness = 18, centerValue, centerLabel }) => {
  const list = (items || []).filter((i) => (i.count || 0) > 0);
  const total = list.reduce((sum, i) => sum + (i.count || 0), 0);

  if (total === 0) return <p className="resident-stats__chart-empty">Nema podataka.</p>;

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const colorOf = (item, idx) => item.color || CHART_COLORS[idx % CHART_COLORS.length];

  let offset = 0;
  return (
    <div className="resident-stats__donut">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="resident-stats__donut-svg"
        role="img"
        aria-label={`Ukupno ${fmt(total)} kreveta`}
      >
        <g transform={`rotate(-90 ${center} ${center})`}>
          {list.map((item, idx) => {
            const dash = ((item.count || 0) / total) * circumference;
            const segment = (
              <circle
                key={`${item.label}-${idx}`}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={colorOf(item, idx)}
                strokeWidth={thickness}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return segment;
          })}
        </g>
        <text x={center} y={center - 2} textAnchor="middle" className="resident-stats__donut-value">
          {centerValue != null ? centerValue : fmt(total)}
        </text>
        {centerLabel && (
          <text x={center} y={center + 14} textAnchor="middle" className="resident-stats__donut-label">
            {centerLabel}
          </text>
        )}
      </svg>

      <ul className="resident-stats__legend">
        {list.map((item, idx) => {
          const pct = Math.round(((item.count || 0) / total) * 100);
          return (
            <li className="resident-stats__legend-item" key={`${item.label}-${idx}`}>
              <span
                className="resident-stats__legend-dot"
                style={{ backgroundColor: colorOf(item, idx) }}
              />
              <span className="resident-stats__legend-label">{item.label}</span>
              <span className="resident-stats__legend-value">
                {fmt(item.count)}
                <span className="resident-stats__legend-pct"> ({pct}%)</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Kolone - pokretljivost stanara
const ColumnChart = ({ items = [], height = 124 }) => {
  const list = items || [];
  if (list.length === 0) return <p className="resident-stats__chart-empty">Nema podataka.</p>;

  const max = Math.max(1, ...list.map((i) => i.count || 0));

  return (
    <div className="resident-stats__columns" style={{ height }}>
      {list.map((item, idx) => {
        const pct = ((item.count || 0) / max) * 100;
        return (
          <div className="resident-stats__column" key={`${item.label}-${idx}`}>
            <span className="resident-stats__column-value">{fmt(item.count)}</span>
            <div className="resident-stats__column-track">
              <div
                className="resident-stats__column-bar"
                style={{
                  height: `${Math.max(4, pct)}%`,
                  backgroundColor: item.color || CHART_COLORS[idx % CHART_COLORS.length],
                }}
              />
            </div>
            <span className="resident-stats__column-label">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};

const ResidentStats = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get('/api/Resident/residents-stats');
        const body = response.data;
        const payload = body && body.data ? body.data : body;
        if (isMounted) setData(payload || null);
      } catch (err) {
        // 404 (nema podataka) nije greška - prikaži prazno stanje
        if (err?.response?.status === 404) {
          if (isMounted) setData(null);
        } else {
          console.error('Greška pri učitavanju statistike doma:', err);
          if (isMounted) setError('Greška pri učitavanju statistike');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="resident-stats">
        {/* Hero skeleton - odgovara bloku "Popunjenost doma" */}
        <div className="resident-stats__skeleton-hero">
          <div className="resident-stats__skeleton-hero-main">
            <span className="resident-stats__skeleton-number" />
            <div className="resident-stats__skeleton-text">
              <span className="resident-stats__skeleton-line" style={{ width: '6rem' }} />
              <span className="resident-stats__skeleton-line resident-stats__skeleton-line--sm" style={{ width: '8rem' }} />
            </div>
          </div>
          <span className="resident-stats__skeleton-bar" />
        </div>

        {/* Kartice kapaciteta skeleton */}
        <div className="resident-stats__grid">
          {[0, 1, 2, 3].map((i) => (
            <div className="resident-stats__skeleton-card" key={i}>
              <span className="resident-stats__skeleton-line" style={{ width: '2.5rem', height: '1.35rem' }} />
              <span className="resident-stats__skeleton-line resident-stats__skeleton-line--sm" style={{ width: '4.5rem' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resident-stats resident-stats--empty">
        <span>{error}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="resident-stats resident-stats--empty">
        <span>Nema podataka o statistici doma</span>
      </div>
    );
  }

  const {
    totalResidents = 0,
    totalBeds = 0,
    takenBeds = 0,
    freeBeds = 0,
    occupancyPercentage = 0,
    mobileCount = 0,
    impairedMobilityCount = 0,
    immobileCount = 0,
  } = data;

  // Svaka kartica vodi na odgovarajuću stranicu:
  // - stanari -> lista štićenika
  // - kreveti -> sobe (slobodni kreveti otvaraju sobe sa filterom "Slobodne")
  const bedStats = [
    { key: 'residents', label: 'Ukupno stanara', value: totalResidents, to: '/residents' },
    { key: 'beds', label: 'Ukupno kreveta', value: totalBeds, to: '/rooms' },
    { key: 'taken', label: 'Zauzeti kreveti', value: takenBeds, to: '/rooms' },
    { key: 'free', label: 'Slobodni kreveti', value: freeBeds, to: '/rooms?onlyAvailable=true' },
  ];

  // Grafikoni - popunjenost kreveta i pokretljivost stanara
  const bedChartItems = [
    { label: 'Zauzeti', count: takenBeds, color: '#9fb746' },
    { label: 'Slobodni', count: freeBeds, color: '#0f62fe' },
  ];

  const mobilityChartItems = [
    { label: 'Pokretni', count: mobileCount, color: '#24a148' },
    { label: 'Otežano pokretni', count: impairedMobilityCount, color: '#f1c21b' },
    { label: 'Nepokretni', count: immobileCount, color: '#da1e28' },
  ];

  return (
    <div className="resident-stats">
      {/* Hero - popunjenost doma (klik za detalje) */}
      <button
        type="button"
        className={`resident-stats__hero ${isExpanded ? 'is-expanded' : ''}`}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <div className="resident-stats__hero-main">
          <span className="resident-stats__hero-number">{occupancyPercentage}%</span>
          <div className="resident-stats__hero-text">
            <span className="resident-stats__hero-title">Popunjenost doma</span>
            <span className="resident-stats__hero-sub">
              {takenBeds} od {totalBeds} {plural(totalBeds, 'krevet', 'kreveta', 'kreveta')} zauzeto
            </span>
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`resident-stats__hero-chevron ${isExpanded ? 'is-expanded' : ''}`}
        />
      </button>

      {/* Kapacitet + grafikoni - prikazuje se na expand */}
      <Collapsible open={isExpanded}>
        <div className="resident-stats__grid">
          {bedStats.map((stat) => (
            <button
              type="button"
              className="resident-stats__card resident-stats__card--clickable"
              key={stat.key}
              onClick={() => navigate(stat.to)}
            >
              <span className="resident-stats__card-value">{stat.value}</span>
              <span className="resident-stats__card-label">{stat.label}</span>
            </button>
          ))}
        </div>

        {/* Grafikoni: popunjenost kreveta (donut) + pokretljivost stanara (kolone) */}
        <div className="resident-stats__charts">
          <div className="resident-stats__chart">
            <span className="resident-stats__chart-title">Popunjenost kreveta</span>
            <DonutChart
              items={bedChartItems}
              centerValue={`${occupancyPercentage}%`}
              centerLabel="popunjeno"
            />
          </div>

          <div className="resident-stats__chart">
            <span className="resident-stats__chart-title">
              Pokretljivost stanara
              <span className="resident-stats__chart-sub"> ({fmt(totalResidents)})</span>
            </span>
            <ColumnChart items={mobilityChartItems} />
          </div>
        </div>

        {/* Prečica do pune statistike: Panel doma → tab „Statistika" */}
        <button
          type="button"
          className="resident-stats__more"
          onClick={() => navigate('/panel-doma?tab=stats')}
        >
          <span className="resident-stats__more-label">
            <ChartLine size={16} />
            Statistika doma
          </span>
          <ArrowRight size={16} className="resident-stats__more-arrow" />
        </button>
      </Collapsible>
    </div>
  );
};

export default ResidentStats;
